import { v4 as uuidv4 } from "uuid";
import { getPDFBase64 } from "./generateW8BENXML";
import { parseAddressComponents } from "../../../utils/formUtils";

export const generateW8ECIXML = async (formData: any) => {
  // Helper function to safely get values
  const getValue = (key: string, defaultValue: string = "") => formData[key] || defaultValue;

  // Helper function to convert boolean to string representation
  const getBooleanValue = (key: string) => {
    const value = formData[key];
    if (
      value === true ||
      value === "true" ||
      value === "selected" ||
      value === "1" ||
      value === 1
    ) {
      return "true";
    }
    return "false";
  };

  // Helper function to determine entity type
  const determineEntityType = () => {
    // Check for new ECI-* keys directly
    if (formData["ECI-Individual"]) return "Individual";
    if (formData["ECI-Corporation"]) return "Corporation";
    if (formData["ECI-Partnership"]) return "Partnership";
    if (formData["ECI-Simple_trust"]) return "Simple trust";
    if (formData["ECI-Grantor_trust"]) return "Grantor trust";
    if (formData["ECI-Complex_trust"]) return "Complex trust";
    if (formData["ECI-Estate"]) return "Estate";
    if (formData["ECI-Private_foundation"]) return "Private foundation";
    if (formData["ECI-Government_integral"]) return "Foreign Government - Integral Part";
    if (formData["ECI-Government_controlled_entity"])
      return "Foreign Government - Controlled Entity";
    if (formData["ECI-International_organization"]) return "International organization";
    if (formData["ECI-Central_bank_of_issue"]) return "Central Bank of Issue";
    if (formData["ECI-Tax_exempt_organization"]) return "Tax-exempt organization";

    // Fallback: Check old Entity Type field for backward compatibility
    const entityType = getValue("Entity Type", "");
    if (entityType) {
      // Map the entity type value to readable format
      const entityMapping: Record<string, string> = {
        ECI_Individual: "Individual",
        ECI_Corporation: "Corporation",
        ECI_Partnership: "Partnership",
        ECI_Simple_trust: "Simple trust",
        ECI_Grantor_trust: "Grantor trust",
        ECI_Complex_trust: "Complex trust",
        ECI_Estate: "Estate",
        ECI_Private_foundation: "Private foundation",
        ECI_Government_integral: "Foreign Government - Integral Part",
        ECI_Government_controlled_entity: "Foreign Government - Controlled Entity",
        ECI_International_organization: "International organization",
        ECI_Central_bank_of_issue: "Central Bank of Issue",
        ECI_Tax_exempt_organization: "Tax-exempt organization",
      };
      return entityMapping[entityType] || entityType;
    }
    return "Individual"; // Default
  };

  // Get beneficial owner name
  const firstName = getValue("First name and middle name", "");
  const lastName = getValue("Last name", "");
  const fullName = getValue("Name", "") || `${firstName} ${lastName}`.trim();

  // Format date properly
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";

    // Try to format the date if it's in a standard format
    try {
      const date = new Date(dateStr);
      return date
        .toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
        .replace(/\//g, "-");
    } catch (e) {
      return dateStr;
    }
  };

  // Generate related documents XML
  const generateRelatedDocumentsXml = async () => {
    let relatedDocumentsXml = "";

    // Helper function to get document type info
    const getDocumentTypeInfo = (documentType: string) => {
      const documentTypeMapping: Record<string, { typeId: string; type: string }> = {
        partnership_agreement: {
          typeId: "74B29767-B166-49A1-90C6-04D919FFF021",
          type: "Partnership Agreement",
        },
        trust_deed: {
          typeId: "E0E34437-CC13-487C-863E-248204E61086",
          type: "Trust Deed or Certified Extracts",
        },
        certificate_registration: {
          typeId: "F0B2D5EC-5C24-4FE5-A571-2610610FA3D1",
          type: "Certificate of Registration",
        },
        certificate_incorporation: {
          typeId: "F113A468-ADE7-4C20-82C4-33A496456F54",
          type: "Certificate of Incorporation",
        },
        armed_forces_card: {
          typeId: "5EBD9B1F-BEB2-4BB5-BCB9-35C105F278A5",
          type: "Armed Forces Card",
        },
        limitations_benefits: {
          typeId: "5309E91F-6B24-4183-9C7F-3A062658E625",
          type: "Limitations of Benefits Statement",
        },
        election_form: {
          typeId: "DB6649B1-F4BE-41D5-AAAD-4588B253C39D",
          type: "Election form - line 9 option",
        },
        public_register: {
          typeId: "58740ED7-F2EF-481D-B0FA-4703F257B1C6",
          type: "Public Register Extracts",
        },
        driving_license: {
          typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8",
          type: "Driving License",
        },
        driverLicense: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
        state_eu: { typeId: "D525BDE1-E0A2-4F2A-B2CE-5AD75383264E", type: "State of the EU" },
        email: { typeId: "C2676B9E-BCFA-497E-8588-6B8B1DA00914", type: "eMail" },
        retroactive_statement: {
          typeId: "3DC45284-ACA4-494B-AA1F-6EBA8736F9E1",
          type: "Retroactive Statement",
        },
        birth_certificate: {
          typeId: "70612B92-D0F3-4347-9CC3-912B9597C800",
          type: "Birth Certificate",
        },
        passport: { typeId: "70825FED-AEBF-4EB8-949D-998D69D718F9", type: "Passport" },
        withholding_statement: {
          typeId: "872ACA95-E43E-4EFA-A494-9A4A97356365",
          type: "Withholding Statement",
        },
        email_alt: { typeId: "325FFDDE-2558-4E32-B7DA-A67435BF59F9", type: "eMail" },
        other: { typeId: "E697B03A-91D1-4CEB-8EC4-A7A4E53E5B30", type: "Other" },
        hybrid_status: {
          typeId: "5A447556-7724-484B-A6D4-BAE392B8FF22",
          type: "Hybrid Status - Supporting Documentation",
        },
        notional_principle: {
          typeId: "BB810C57-8652-4431-BEB2-BE50057C17DC",
          type: "Notional principle contracts statement",
        },
        connected_documentation: {
          typeId: "ACCDB44A-2518-4B06-95C2-BF837E64E343",
          type: "Connected documentation",
        },
        national_id_member: {
          typeId: "4EA07713-50FD-4427-AB0A-C3972E3A7BB0",
          type: "National ID Card of A Member",
        },
        form_w4: { typeId: "D9AFD794-205A-45C3-A64A-C5219F20CBB2", type: "Form W-4" },
        proof_residency: {
          typeId: "F1BC6DC5-19C6-441F-A133-CEDE22778968",
          type: "Proof of Residency Document",
        },
        affidavit: { typeId: "B6477F29-2FE4-4CC0-A58E-CF7B91F791E7", type: "Affidavit" },
        national_id: { typeId: "405AE4A8-A1FA-45EB-9449-D1C76EC101FB", type: "National ID Card" },
        effectively_connected: {
          typeId: "BD2028C8-56A5-4404-895A-D36F42535DDB",
          type: "Effectively connected income statement - line 9 option",
        },
        power_attorney: {
          typeId: "59AC92CD-62C8-46DC-BC33-DBAFA0716242",
          type: "Power of attorney statement",
        },
        us_counsel: {
          typeId: "B6A5ABBA-E61E-4C5C-9B43-E051D75A26F6",
          type: "U.S. counsel opinion statement",
        },
        work_visa: { typeId: "1F7944EA-CF76-40FB-B182-E3D1DE59A74A", type: "Work visa" },
        expatriate: {
          typeId: "7AE8E536-21FD-46A2-93B7-EE99477D7DE2",
          type: "Expatriate Documentation",
        },
      };

      // Normalize the document type by removing common prefixes and converting to lowercase
      const normalizedType = documentType
        .toLowerCase()
        .replace(/^idDocument\./, "")
        .replace(/\.pdf$/, "");

      // Try direct match first
      if (documentTypeMapping[normalizedType]) {
        return documentTypeMapping[normalizedType];
      }

      // Try partial matches for common document types
      if (normalizedType.includes("passport")) {
        return documentTypeMapping["passport"];
      }
      if (normalizedType.includes("driver") || normalizedType.includes("license")) {
        return documentTypeMapping["driving_license"];
      }
      if (normalizedType.includes("national") && normalizedType.includes("id")) {
        return documentTypeMapping["national_id"];
      }

      // Default to 'Other' if no match found
      return documentTypeMapping["other"];
    };

    // Handle single document case from original code
    if (formData.idDocumentUrl) {
      const docContent = await getPDFBase64(formData.idDocumentUrl);
      const docTypeInfo = getDocumentTypeInfo(formData.idDocumentType || "other");

      relatedDocumentsXml += `
			<RelatedDocument>
				<ID>${uuidv4()}</ID>
				<Type>${docTypeInfo.type}</Type>
				<TypeID>${docTypeInfo.typeId}</TypeID>
				<File>
					<Name>${formData.idDocumentType || "id_document"}.pdf</Name>
					<Data>${docContent}</Data>
				</File>
			</RelatedDocument>`;
    }

    // Handle multiple documents if provided in docs array
    if (formData.docs && Array.isArray(formData.docs)) {
      for (const doc of formData.docs) {
        if (doc.url) {
          const docContent = await getPDFBase64(doc.url);
          const docTypeInfo = getDocumentTypeInfo(doc.type || "other");

          relatedDocumentsXml += `
					<RelatedDocument>
						<Type>${docTypeInfo.type}</Type>
						<TypeID>${docTypeInfo.typeId}</TypeID>
						<File>
							<Name>${doc.name || "document.pdf"}</Name>
							<Data>${docContent}</Data>
						</File>
					</RelatedDocument>`;
        }
      }
    }

    return relatedDocumentsXml;
  };

  // Helper function to get income details with fallback
  const getIncomeDetails = () => {
    // First try to get from income items array (index 0)
    const incomeText0 = getValue("ECI_income_text_0", "");
    if (incomeText0.trim() !== "") {
      return incomeText0;
    }

    // Fallback to ECI-11_Effectively_connected_income_details field
    return getValue("ECI-11_Effectively_connected_income_details", "");
  };

  const relatedDocumentsXml = await generateRelatedDocumentsXml();

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf", "");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formData.fileImportId);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
				<OriginalFile>
					<Name>${fileName}</Name>
					<Data>${originalFileContent}</Data>
				</OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();
  const submissionDate = formatDate(getValue("Signature_date", ""));

  // Parse permanent address components if ECI-Business_address_city_state_zip is not empty
  let businessAddressParsed = { city: "", state: "", postalCode: "" };
  const businessCityAddress = getValue("ECI-Business_address_city_state_zip", "");
  if (businessCityAddress && businessCityAddress.trim() !== "") {
    businessAddressParsed = parseAddressComponents(businessCityAddress);
  }

  // Parse permanent city address if available and not already parsed
  let permanentCityAddressField: { city?: string; state?: string; postalCode?: string } = {};
  const permanentCityAddress = getValue("Permanent city address", "");
  if (permanentCityAddress && permanentCityAddress.trim() !== "") {
    permanentCityAddressField = parseAddressComponents(permanentCityAddress);
  }

  const xml = `<W_8ECI_OCT_2021>
	<UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
	<UIDreferenceline>False</UIDreferenceline>
	<Instructor_Identifier>${getValue("UniqueIdentifier")}</Instructor_Identifier>
	<ElectronicRecipientStatement>True</ElectronicRecipientStatement>
	<ElectronicRecipientStatementDate>${submissionDate}</ElectronicRecipientStatementDate>
			<Entity>${getValue("ECI-Disregarded_entity_name", "")}</Entity>
		<CertifyDealerinSecurites>${getValue("ECI-12_Securities_dealer", "") === "selected" ? true : false}</CertifyDealerinSecurites>
	<CommonInfo>
		<EntityTypes>
			<EntityType>${determineEntityType()}</EntityType>
		</EntityTypes>
		<EntityClassification/>
		<InitialEntityTypeSelection>Non U.S. Entity</InitialEntityTypeSelection>
		<FATCAEntityType/>
		<Individual>
			<FirstName>${getValue("First name and middle name", "") || getValue("Beneficial Owner name", "").split(" ")[0]}</FirstName>
			<LastName>${getValue("Last name", "") || getValue("Beneficial Owner name", "").split(" ")[1]}</LastName>
			<DateOfBirth>${getValue("Date of birth", "")}</DateOfBirth>
			<CountryOfBirth>${getValue("Country_of_incorporation_organization", "")}</CountryOfBirth>
			<CountryOfBirthOther/>
			<CountryOfCitizenshipForTax>${getValue("Country_of_incorporation_organization", "")}</CountryOfCitizenshipForTax>
			<CountryOfCitizenshipForTaxOther/>
			<CityTownOfBirth/>
			<PlaceofBirth/>
			<AdditionalTaxJurisdictions/>
		</Individual>
		<Business>
			<Name>${getValue("Name", "") || getValue("Beneficial Owner name", "")}</Name>
			<Country>${getValue("Country_of_incorporation_organization", "")}</Country>
		<CountryOther/>
		<DoingBusinessAsName>${getValue("ECI-Disregarded_entity_name", "")}</DoingBusinessAsName>
		<DREFATCAEntitytype>${getValue("Other", "")}</DREFATCAEntitytype>
		<JurisdictionTaxCountry>${getValue("Country_of_incorporation_organization", "")}</JurisdictionTaxCountry>
			<SponsoringEntityName/>
			<SponsoringEntityGIIN/>
			<TrusteeName/>
			<TrusteeGIIN/>
			<AdditionalTaxJurisdictions/>
			<IsSpecifiedUSPerson>true</IsSpecifiedUSPerson>
			<SpecifiedPersonUS>NA</SpecifiedPersonUS>
			<IsSpecifiedUKPerson>true</IsSpecifiedUKPerson>
			<SpecifiedPersonUK>NA</SpecifiedPersonUK>
			<IsSpecifiedCRSPerson>true</IsSpecifiedCRSPerson>
			<SpecifiedPersonCRS>NA</SpecifiedPersonCRS>
		</Business>
		<PermanentAddress>
			<Line1>${getValue("Permanent residence address", "")}</Line1>
			<Line2>${getValue("Permanent_address_line_2", "")}</Line2>
			<Line3>${getValue("Permanent_address_line_3", "")}</Line3>
			<City_Town>${permanentCityAddressField.city || getValue("Permanent_address_city", "")}</City_Town>
			<State_Province>${permanentCityAddressField.state || getValue("Permanent_address_state_USCA", "") || getValue("Permanent_address_state_NonUS", "")}</State_Province>
			<Country>${getValue("Permanent address country", "")}</Country>
			<CountryOther>${getValue("Permanent_address_other", "")}</CountryOther>
			<Zip_PostalCode>${permanentCityAddressField.postalCode || getValue("Permanent_address_zip", "")}</Zip_PostalCode>
			<IsCare_of_address>${getValue("Permanent_address_is_care_of", "") === "yes" ? "True" : "False"}</IsCare_of_address>
			<IsP_O_Box>${getValue("Permanent_address_is_po_box", "") === "yes" ? "True" : "False"}</IsP_O_Box>
			<IsRuralRoute>${getValue("Permanent_address_is_rural_route", "") === "yes" ? "True" : "False"}</IsRuralRoute>
		</PermanentAddress>
		<MailingAddress>
			<Line1>${getValue("ECI-Business_address_street", "")}</Line1>
			<Line2>${getValue("ECI-Business_address_line_2", "")}</Line2>
			<Line3>${getValue("ECI-Business_address_line_3", "")}</Line3>
			<City_Town>${businessAddressParsed.city || getValue("Business_address_city", "")}</City_Town>
			<State_Province>${businessAddressParsed.state || getValue("Business_address_state_USCA", "") || getValue("Business_address_state_NonUS", "")}</State_Province>
			<Country>${getValue("Business_address_country", "")}</Country>
			<CountryOther>${getValue("Business_address_other", "")}</CountryOther>
			<Zip_PostalCode>${businessAddressParsed.postalCode || getValue("Business_address_zip", "")}</Zip_PostalCode>
			<IsCare_of_address>${getValue("Business_address_is_care_of", "") === "yes" ? "True" : "False"}</IsCare_of_address>
			<IsP_O_Box>${getValue("Business_address_is_po_box", "") === "yes" ? "True" : "False"}</IsP_O_Box>
			<IsRuralRoute>${getValue("Business_address_is_rural_route", "") === "yes" ? "True" : "False"}</IsRuralRoute>
		</MailingAddress>
		<ContactInfo>
			<FirstName>${getValue("First name and middle name", "")}</FirstName>
			<LastName>${getValue("Last name", "")}</LastName>
			<Email>${getValue("Taxpayer_email", "")}</Email>
			<SignatoryEmail/>
			<TelephoneCountry>${getValue("Country_of_incorporation_organization", "India")}</TelephoneCountry>
			<TelephoneNumber/>
			<CellNumber/>
		</ContactInfo>
		<SubsidiaryInfo>
			<SubsidiaryId/>
			<SubsidiaryName/>
			<SubsidiaryContactName/>
			<SubsidiaryContactTelephone/>
			<SubsidiaryContactEmail/>
		</SubsidiaryInfo>
		<HybridStatus>Not Applicable</HybridStatus>
		<HybridStatusDescription/>
		<HybridStatusSupportingDocumentationAttached>False</HybridStatusSupportingDocumentationAttached>
		<CitizenshipInfo>
			<IsUSPerson/>
			<IsUSGreenCardHolder/>
			<IsDualCitizenshipHolder/>
			<DoesDualCitizenshipIncludeAmerica/>
			<IsBornOrganisedInUS/>
			<HasRenouncedCitizenship/>
			<DateRenouncedCitizenship/>
			<AreCountryForTaxAndBirthDifferent/>
			<InUSA31DaysCurrentYear/>
		</CitizenshipInfo>
		<IsOnBehalfOfSomeoneElse></IsOnBehalfOfSomeoneElse>
		<Proxy>
			<FirstName>${getValue("First name and middle name", "")}</FirstName>
			<LastName>${getValue("Last name", "")}</LastName>
			<Capacity>${getValue("Signing_capacity", "") == "selected" ? true : false}</Capacity>
			<CapacityOther/>
			<PrimaryContactNumber/>
		</Proxy>
		<NoUSSiDeclarationConfirmed/>
		<NoUSSiDeclarationPart2_3Skipped>False</NoUSSiDeclarationPart2_3Skipped>
		<IgnoredRules>
			<IgnoredRule>
				<Rule>TIN121</Rule>
				<Comment/>
			</IgnoredRule>
			<IgnoredRule>
				<Rule>IRSN101</Rule>
				<Comment/>
			</IgnoredRule>
			<IgnoredRule>
				<Rule>SIG112</Rule>
				<Comment/>
			</IgnoredRule>
		</IgnoredRules>
		<NonUSIndividual_IsClaimingECI>True</NonUSIndividual_IsClaimingECI>
		<NonUSIndividual_IsForIndependentPersonalServices/>
		<NonUSIndividual_IsPublicEntertainer/>
		<NonUSIndividual_IsResidentNoSpecificProvision/>
		<NonUSIndividual_IsForDependentPersonalServices/>
		<NonUSIndividual_IsForScholarshipFellowshipAndPersonalServicesIncome/>
		<NonUSIndividual_PartOfWhichNotClaimingTaxTreatyWithholdingExemption/>
		<NonUSIndividual_IsNotClaimingTaxTreatyWithholdingExemption/>
		<NonUSIndividual_IsForScholarshipFellowshipNoPersonalServicesIncome/>
		<NonUSIndividual_IsForOnlyForeignStatusOrTreatyBenefits/>
		<SubmissionGroupId>${uuidv4()}</SubmissionGroupId>
		<SelectedOptionsonGuide4/>
	</CommonInfo>
	<TaxpayerIdentification>
		<Type>${(getBooleanValue("ECI-Us_tin_SSN/ITIN") === "true" ? "SSN/ITIN" : getBooleanValue("ECI-EIN") === "true" ? "EIN" : "") || "EIN"}</Type>
		<GIINAppliedFor>False</GIINAppliedFor>
		<GIINNotApplicable/>
		<EIN>${getValue("U.S. taxpayer identification number", "")}</EIN>
		<SSN>${getValue("U.S. taxpayer identification number", "")}</SSN>
		<TINFormatNotAvail>False</TINFormatNotAvail>
		<ForeignTaxpayerId>${getValue("Foreign tax identifying number", "")}</ForeignTaxpayerId>
		<ForeignTinUnavailable>${getBooleanValue("FTIN_not_legally_required")}</ForeignTinUnavailable>
		<ForeignTinUnavailableReason>${getBooleanValue("FTIN_not_legally_required") === "true" ? "Not legally required" : ""}</ForeignTinUnavailableReason>
		<IsAppliedForTIN>False</IsAppliedForTIN>
		<UnexpectedTINTypeExplanation/>
		<UnexpectedTINTypeExplanationConfirmed/>
		<UnexpectedTINTypeContentBlock/>
		<CheckifFTINnotlegallyrequired>${getBooleanValue("FTIN_not_legally_required")}</CheckifFTINnotlegallyrequired>
		<IsFurtherExplanation>False</IsFurtherExplanation>
		<WrittenStatementReasons/>
		<WrittenStatementReasonsAdditionalInformation/>
		<VATNumber>${getValue("Foreign tax identifying number", "")}</VATNumber>
		<TINCheck>
			<IsCheckPreformed>False</IsCheckPreformed>
			<IsSuccessful>False</IsSuccessful>
			<Date/>
		</TINCheck>
	</TaxpayerIdentification>
	<SignInfo>
		<IsSigned>${getBooleanValue("isSigned") || (getBooleanValue("isMapDone") === "false" ? (getValue("Signature", "").trim() !== "" ? true : false) : false)}</IsSigned>
		<IsModified>${getBooleanValue("isModified")}</IsModified>
		<By>${getValue("Print_name", "")}</By>
		<Capacity>${getBooleanValue("Signing_capacity")}</Capacity>
		<SignatureUnreadable>${getBooleanValue("Signature_unreadable")}</SignatureUnreadable>
		<UserLocalTime/>
		<UserLocalTimeZone/>
		<Date>${submissionDate}</Date>
		<EnteredConfirmationCode>HNPDGK</EnteredConfirmationCode>
	</SignInfo>
	<Tag>
		<SubmissionDate>${submissionDate}</SubmissionDate>
		<IsFromComplyExchangeService>True</IsFromComplyExchangeService>
		<IsFromBureau>False</IsFromBureau>
		<IsPaperForm>False</IsPaperForm>
		<IsE_form>False</IsE_form>
		<IsMobileSubmission>False</IsMobileSubmission>
		<RetroactiveStatement>
			<IsSigned/>
			<SignedBy/>
			<SignDate>${submissionDate}</SignDate>
			<EffectiveDate>${submissionDate}</EffectiveDate>
			<ChangeinCircumstanceDate/>
			<ReasonForChange/>
			<Explanation/>
			<RetroSignBy/>
			<StatementText>I declare, under penalties of perjury, that I have examined the Form W-8ECI submission that I am providing and that the information and certifications contained therein remain the same and unchanged throughout the period beginning on 01/01/2014 to the date of the submission here and that they were true, correct and complete during the entire period.&amp;lt;br /&amp;gt;&amp;lt;br /&amp;gt;If any information or certifications were not true, correct and complete for that entire period enter the date from when they were, providing additional written information below supporting the changes in circumstances back to the account opening date provided above.</StatementText>
		</RetroactiveStatement>
	</Tag>	
	<RelatedDocuments>
		${relatedDocumentsXml}
	</RelatedDocuments>
	${originalFileXml}
			<ReferenceNumbers>${getValue("ECI-Reference_number", "")}</ReferenceNumbers>
		<ItemsOfIncome>${getValue("ECI_income_text_0", "") ?? getValue("ECI-11_Effectively_connected_income_details", "")}</ItemsOfIncome>
	<IncomeReports>
		<IncomeType>${getValue("ECI_income_type_0", "")}</IncomeType>
		<IncomeDescription>${getValue("ECI_income_text_0", "")}</IncomeDescription>
		<IncomeAmount>${getValue("ECI_income_amount_0", "")}</IncomeAmount>
	</IncomeReports>
	<SubstituteFormStatement>The Internal Revenue Service does not require your consent to any provisions of this document other than the certifications required to avoid backup withholding.</SubstituteFormStatement>
	<SignatureCertify>${getBooleanValue("Signing_capacity")}</SignatureCertify>
</W_8ECI_OCT_2021>`;
  console.log("Generated W-8ECI XML:", xml);
  return xml;
};
